'use client';

import { Suspense, useEffect, useLayoutEffect, useMemo, useState, type ReactNode } from 'react';
import { useSearchParams } from 'next/navigation';
import { VisualizationDiagnosticsProvider, setXrDiagnosticReporter } from '@toolproof-core/visualization/runtime/diagnostics';

import developerConfig from '@/_data/developerConfig.json' with { type: 'json' };
import { useAppSelector } from '@/integrations/redux/useReduxStore';
import { selectDeveloperMode } from '@/integrations/redux/slices/configSlice';
import { publishCosmosDiagnostics } from '@/diagnosticsState';
import { resolveCosmosDiagnostics } from '@/_pure/diagnostics';
import { CosmosDiagnosticsContext } from '@/CosmosDiagnosticsContext';

function DiagnosticQuery({ onChange }: { onChange: (query: string) => void }) {
    const query = useSearchParams().toString();
    useLayoutEffect(() => onChange(query), [query, onChange]);
    return null;
}

export function CosmosDiagnosticsProvider({ children }: { children: ReactNode }) {
    const developerMode = useAppSelector(selectDeveloperMode);
    const [query, setQuery] = useState('');
    const { options, errors } = useMemo(() => resolveCosmosDiagnostics(
        developerMode, developerConfig.diagnostics, new URLSearchParams(query),
    ), [developerMode, query]);
    useLayoutEffect(() => publishCosmosDiagnostics(options), [options]);
    // XR locomotion samples go to the Forge server log, where they can be read without the headset.
    useEffect(() => options.xrLocomotion ? setXrDiagnosticReporter(sample => {
        void fetch('/api/agent/xr-diagnostics', {
            method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(sample), keepalive: true,
        }).then(response => {
            if (!response.ok) console.error('[xr-diagnostics] Upload rejected:', response.status);
        }).catch(() => console.error('[xr-diagnostics] Upload failed.'));
    }) : undefined, [options.xrLocomotion]);
    const visualizationOptions = useMemo(() => ({
        xrLocomotion: options.xrLocomotion,
        ringGeometry: options.ringGeometry,
        cameraGraph: options.cameraGraph,
        guidance: options.episode,
        cameraInspection: options.cameraInspection,
    }), [options]);
    return <CosmosDiagnosticsContext.Provider value={options}>
        <VisualizationDiagnosticsProvider options={visualizationOptions}>
            <Suspense fallback={null}><DiagnosticQuery onChange={setQuery} /></Suspense>
            {errors.length > 0 ? <div role="alert" style={{
                position: 'absolute', top: 12, right: 12, zIndex: 110,
                background: '#7f1d1d', color: '#ffffff', padding: 12,
            }}>Invalid diagnostic configuration: {errors.join(' ')}</div> : null}
            {children}
        </VisualizationDiagnosticsProvider>
    </CosmosDiagnosticsContext.Provider>;
}
